import styled from 'styled-components';

import type { KeyboardEvent } from 'react';

interface SwitchProps {
  /** The visible label after the track. Empty when the row around the switch already names it. */
  label: string;
  /** The accessible name when `label` is empty. */
  ariaLabel?: string;
  checked: boolean;
  onClick: () => void;
  disabled?: boolean;
  labelColor?: string;
  checkedColor?: string;
  labelSize?: string;
  small?: boolean;
  /** Smaller again (26×14 track): for a row of 14px sliders, where `small` still reads as the biggest thing on the line. */
  tiny?: boolean;
}

export default function Switch({
  label,
  ariaLabel,
  checked,
  onClick,
  disabled,
  labelColor,
  checkedColor,
  labelSize,
  small,
  tiny,
}: SwitchProps) {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <SwitchContainer
      role="switch"
      aria-checked={checked}
      aria-label={label ? undefined : ariaLabel}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
    >
      <Track
        checked={checked}
        disabled={disabled}
        $checkedColor={checkedColor}
        $small={small}
        $tiny={tiny}
      >
        <Thumb checked={checked} $small={small} $tiny={tiny} />
      </Track>
      {label && (
        <SwitchLabel color={labelColor} $size={labelSize}>
          {label}
        </SwitchLabel>
      )}
    </SwitchContainer>
  );
}

const SwitchContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  user-select: none;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
`;

const Track = styled.div<{
  checked: boolean;
  disabled?: boolean;
  $checkedColor?: string;
  $small?: boolean;
  $tiny?: boolean;
}>`
  position: relative;
  width: ${props => (props.$tiny ? '26px' : props.$small ? '32px' : '40px')};
  height: ${props => (props.$tiny ? '14px' : props.$small ? '18px' : '24px')};
  background: ${props =>
    props.checked ? props.$checkedColor || 'var(--accent)' : 'var(--border)'};
  border-radius: ${props =>
    props.$tiny ? '7px' : props.$small ? '9px' : '12px'};
  transition: background var(--transition-normal);
  box-sizing: border-box;
`;

const Thumb = styled.div<{
  checked: boolean;
  $small?: boolean;
  $tiny?: boolean;
}>`
  position: absolute;
  top: ${props => (props.$tiny ? '2px' : props.$small ? '2px' : '3px')};
  left: ${props =>
    props.checked
      ? props.$tiny
        ? '14px'
        : props.$small
          ? '16px'
          : '19px'
      : props.$tiny
        ? '2px'
        : props.$small
          ? '2px'
          : '3px'};
  width: ${props => (props.$tiny ? '10px' : props.$small ? '14px' : '18px')};
  height: ${props => (props.$tiny ? '10px' : props.$small ? '14px' : '18px')};
  background: var(--background);
  border-radius: 50%;
  transition: left var(--transition-normal);
`;

const SwitchLabel = styled.div<{ color?: string; $size?: string }>`
  text-transform: none;
  text-align: center;
  font-family: var(--font-primary);
  font-size: ${props => props.$size || 'var(--text-xs)'};
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-xs);
  color: ${props => props.color || 'var(--text-secondary)'};
`;
